// src/utils/vehicleAssignmentReports.ts — Exportable weekly / full records of
// vehicle & plant → employee assignment activity, kept for future reference.
import type { AssignmentHistoryEntry, CurrentAssignment } from './vehicleAssignments'
import { getWeekRange } from './vehicleAssignments'
import { formatDateTime } from './formatters'

function downloadText(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}

function csvCell(value: string | number | null | undefined) {
  const text = value == null ? '' : String(value)
  return `"${text.split('"').join('""')}"`
}

const HISTORY_HEADERS = [
  'Recorded At', 'Action', 'Asset Number', 'Asset Name', 'Category',
  'Employee', 'Previous Employee', 'Effective Date', 'Reason', 'Recorded By',
]

function historyRows(entries: AssignmentHistoryEntry[]) {
  return entries.map(h => [
    h.recordedAt,
    h.action,
    h.assetNumber,
    h.assetName,
    h.category,
    h.employeeName ?? '(unassigned)',
    h.previousEmployeeName ?? '',
    h.effectiveDate,
    h.reason,
    h.recordedByName,
  ])
}

function buildCsv(entries: AssignmentHistoryEntry[]) {
  const rows = [HISTORY_HEADERS, ...historyRows(entries)]
  return rows.map(row => row.map(csvCell).join(',')).join('\n')
}

/** Downloads a CSV of assignment activity for the current (or given) week — the "weekly file". */
export function downloadWeeklyAssignmentCsv(entries: AssignmentHistoryEntry[], reference: Date = new Date()) {
  const { label } = getWeekRange(reference)
  const csv = buildCsv(entries)
  const filename = `Vehicle_Plant_Assignments_Week_${label}.csv`
  downloadText(csv, filename, 'text/csv;charset=utf-8')
  return filename
}

/** Downloads a JSON manifest of assignment activity for the current (or given) week. */
export function downloadWeeklyAssignmentJson(entries: AssignmentHistoryEntry[], reference: Date = new Date()) {
  const { start, end, label } = getWeekRange(reference)
  const manifest = {
    weekStart: start.toISOString(),
    weekEnd: end.toISOString(),
    generatedAt: new Date().toISOString(),
    totalEvents: entries.length,
    assignments: entries.filter(e => e.action === 'ASSIGN').length,
    reassignments: entries.filter(e => e.action === 'REASSIGN').length,
    unassignments: entries.filter(e => e.action === 'UNASSIGN').length,
    entries,
  }
  const filename = `Vehicle_Plant_Assignments_Week_${label}.json`
  downloadText(JSON.stringify(manifest, null, 2), filename, 'application/json;charset=utf-8')
  return filename
}

/** Downloads the complete, all-time assignment history log as CSV — kept for future reference. */
export function downloadFullAssignmentHistoryCsv(entries: AssignmentHistoryEntry[]) {
  const csv = buildCsv(entries)
  const filename = `Vehicle_Plant_Assignment_History_Full_${new Date().toISOString().slice(0, 10)}.csv`
  downloadText(csv, filename, 'text/csv;charset=utf-8')
  return filename
}

/** Downloads a snapshot of who currently holds what, right now. */
export function downloadCurrentAssignmentsCsv(current: CurrentAssignment[]) {
  const headers = ['Asset Number', 'Asset Name', 'Category', 'Employee', 'Assigned Since', 'Reason', 'Recorded By', 'Recorded At']
  const rows = current
    .slice()
    .sort((a, b) => a.assetNumber.localeCompare(b.assetNumber))
    .map(a => [a.assetNumber, a.assetName, a.category, a.employeeName, a.effectiveDate, a.reason, a.recordedByName, a.recordedAt])
  const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n')
  const filename = `Vehicle_Plant_Current_Assignments_${new Date().toISOString().slice(0, 10)}.csv`
  downloadText(csv, filename, 'text/csv;charset=utf-8')
  return filename
}

export function formatRecordedAt(iso: string) {
  return formatDateTime(iso)
}
